
import React from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const BAHComparisonTexasInstallationsPage = () => {
  const getIsoDate = () => {
    try {
      return new Date().toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Fort Bliss vs Fort Cavazos vs JBSA BAH Rates 2025-2026 Comparison | TREX RE LLC"
        description="Side-by-side BAH rate comparison for Fort Bliss, Fort Cavazos, and Joint Base San Antonio for 2025 and 2026. See every rank, housing cost differences, cost of living analysis, and which Texas installation gives you the most BAH surplus."
        keywords="fort bliss bah rates 2026, fort cavazos bah rates, jbsa bah rates, texas military bah comparison, bah rates san antonio, bah rates killeen, fort bliss vs fort cavazos, military housing texas"
        canonical="/military/bah-comparison-texas-installations"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Fort Bliss vs Fort Cavazos vs JBSA: Texas BAH Rates Compared 2025-2026",
          "description": "Comprehensive side-by-side comparison of BAH rates at Fort Bliss, Fort Cavazos, and Joint Base San Antonio for 2025 and 2026 with housing cost analysis and surplus calculations.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "datePublished": "2025-01-30",
          "dateModified": getIsoDate(),
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://trexrealestate.com/military/bah-comparison-texas-installations"
          }
        }}
      />

      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b11ca913a0cc31975c31e21aaef1bce1.jpg"
            alt="Texas military installations Fort Bliss Fort Cavazos and JBSA BAH rates comparison aerial view"
            title="Fort Bliss vs Fort Cavazos vs JBSA BAH rates 2025 2026 Texas comparison"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
            Texas BAH Comparison Guide
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Fort Bliss vs Fort Cavazos vs JBSA: BAH Rates Compared 2025–2026
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
            Side-by-side comparison of Basic Allowance for Housing at Texas&apos;s three largest military installations. Every rank, actual housing costs, cost of living analysis, and which base gives you the biggest BAH surplus.
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>By TREX RE LLC</span>
            <span>&bull;</span>
            <span>January 2025</span>
            <span>&bull;</span>
            <span>22 min read</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">In This Guide</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <a href="#introduction" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Why Compare Texas BAH Rates</span>
            </a>
            <a href="#installation-overview" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Three Installations at a Glance</span>
            </a>
            <a href="#enlisted-bah-2025" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>2025 Enlisted BAH Comparison</span>
            </a>
            <a href="#officer-bah-2025" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>2025 Officer BAH Comparison</span>
            </a>
            <a href="#enlisted-bah-2026" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>2026 Enlisted BAH Comparison</span>
            </a>
            <a href="#officer-bah-2026" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>2026 Officer BAH Comparison</span>
            </a>
            <a href="#housing-costs" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Actual Housing Costs by City</span>
            </a>
            <a href="#bah-surplus" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>BAH Surplus Analysis</span>
            </a>
            <a href="#cost-of-living" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Cost of Living Beyond Housing</span>
            </a>
            <a href="#rent-vs-buy" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Rent vs Buy at Each Installation</span>
            </a>
            <a href="#pcs-strategy" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>PCS Strategy &amp; Rate Protection</span>
            </a>
            <a href="#faq" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>FAQ: Texas BAH Comparison</span>
            </a>
          </div>
        </div>
      </section>

      <ArticlePartOne />
      <ArticlePartTwo />
      <ArticlePartThree />
      <ArticlePartFour />

      <Footer />
    </div>
  );
};

export default BAHComparisonTexasInstallationsPage;
